import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { INCOMING_STATUS, UPLOAD_BASE, beYearOptions, currentBeYear, fmtDateTH } from './aoShared'

const emptyForm = {
  in_received_date: '', in_doc_no: '', in_doc_date: '',
  in_source: '', in_subject: '', in_to: '', in_detail: '',
}

export default function IncomingDocs() {
  const navigate = useNavigate()
  const [year, setYear] = useState(String(currentBeYear()))
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (year) params.set('year', year)
      if (status) params.set('status', status)
      if (q) params.set('q', q)
      const res = await axiosInstance.get(`/api/ao/incoming?${params.toString()}`)
      if (res.data.success) setDocs(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year, status, q])

  useEffect(() => { fetchData() }, [fetchData])

  const openModal = () => {
    setForm({ ...emptyForm, in_received_date: new Date().toLocaleDateString('en-CA') })
    setFile(null)
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''))
      if (file) fd.append('file', file)
      const res = await axiosInstance.post('/api/ao/incoming', fd)
      if (res.data.success) {
        setShowModal(false)
        const { in_full_no, in_id } = res.data.data
        const goDetail = await Swal.fire({
          icon: 'success',
          title: `ลงรับเรียบร้อย เลขรับ ${in_full_no}`,
          text: 'ต้องการเกษียน/เวียนหนังสือต่อเลยหรือไม่?',
          showCancelButton: true,
          confirmButtonText: 'เกษียน/เวียนต่อ',
          cancelButtonText: 'ปิด',
        })
        if (goDetail.isConfirmed) navigate(`/admin-office/incoming/${in_id}`)
        else fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-inbox-archive-line me-2 text-primary" />
              ทะเบียนหนังสือรับ
            </h4>
            <Button variant="primary" size="sm" onClick={openModal}>
              <i className="ri-add-line me-1" />ลงรับหนังสือ
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
              <Form.Select size="sm" style={{ width: 150 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {Object.entries(INCOMING_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Form.Select>
              <Form.Control size="sm" style={{ maxWidth: 260 }} placeholder="ค้นหา เลขรับ/เลขที่หนังสือ/หน่วยงาน/เรื่อง"
                value={q} onChange={e => setQ(e.target.value)} />
              <div className="ms-auto text-muted fs-sm">{docs.length} รายการ</div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : docs.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบหนังสือรับในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 90 }}>เลขรับ</th>
                      <th style={{ width: 110 }}>วันที่ลงรับ</th>
                      <th>เลขที่หนังสือ</th>
                      <th>จาก</th>
                      <th>เรื่อง</th>
                      <th className="text-center" style={{ width: 100 }}>รับทราบ</th>
                      <th style={{ width: 60 }}>ไฟล์</th>
                      <th style={{ width: 110 }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map(d => {
                      const st = INCOMING_STATUS[d.in_status] || INCOMING_STATUS.pending
                      return (
                        <tr key={d.in_id} style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin-office/incoming/${d.in_id}`)}>
                          <td className="fw-semibold">{d.in_full_no}</td>
                          <td>{fmtDateTH(d.in_received_date)}</td>
                          <td>{d.in_doc_no || '-'}</td>
                          <td className="text-truncate" style={{ maxWidth: 160 }} title={d.in_source}>{d.in_source}</td>
                          <td className="text-truncate" style={{ maxWidth: 260 }} title={d.in_subject}>
                            <Link to={`/admin-office/incoming/${d.in_id}`} onClick={e => e.stopPropagation()}>{d.in_subject}</Link>
                          </td>
                          <td className="text-center">
                            {d.route_count > 0 ? `${d.ack_count}/${d.route_count}` : '-'}
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            {d.in_file ? (
                              <a href={`${UPLOAD_BASE}/uploads/ao_docs/${d.in_file}`} target="_blank" rel="noreferrer" title="เปิดไฟล์">
                                <i className="ri-attachment-2" />
                              </a>
                            ) : '-'}
                          </td>
                          <td><Badge bg={st.bg}>{st.label}</Badge></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      {/* Register-incoming modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">ลงรับหนังสือ (ออกเลขรับอัตโนมัติ)</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Row className="g-2 mb-2">
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">วันที่ลงรับ <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="date" required value={form.in_received_date}
                  onChange={e => setForm(f => ({ ...f, in_received_date: e.target.value }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">เลขที่หนังสือ (ต้นทาง)</Form.Label>
                <Form.Control size="sm" type="text" maxLength={100} value={form.in_doc_no}
                  onChange={e => setForm(f => ({ ...f, in_doc_no: e.target.value }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">ลงวันที่ (ต้นทาง)</Form.Label>
                <Form.Control size="sm" type="date" value={form.in_doc_date}
                  onChange={e => setForm(f => ({ ...f, in_doc_date: e.target.value }))} />
              </Col>
            </Row>
            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">จากหน่วยงาน <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} required value={form.in_source}
                  onChange={e => setForm(f => ({ ...f, in_source: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">เรียน</Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} placeholder="เช่น ผู้อำนวยการโรงเรียน" value={form.in_to}
                  onChange={e => setForm(f => ({ ...f, in_to: e.target.value }))} />
              </Col>
            </Row>
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">เรื่อง <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={500} required value={form.in_subject}
                onChange={e => setForm(f => ({ ...f, in_subject: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">รายละเอียด <span className="text-muted">— ไม่บังคับ</span></Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} value={form.in_detail}
                onChange={e => setForm(f => ({ ...f, in_detail: e.target.value }))} />
            </Form.Group>
            <Form.Group>
              <Form.Label className="fs-sm mb-1">ไฟล์สแกน <span className="text-muted">— PDF/Word/รูปภาพ ไม่เกิน 10MB</span></Form.Label>
              <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                onChange={e => setFile(e.target.files[0] || null)} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-inbox-archive-line me-1" />}
              ลงรับหนังสือ
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
